import React, { useEffect, useState } from 'react';
import PropTypes from "prop-types";
import { isSameDay } from "date-fns";
import { dateFromState } from "../../../../Helpers/DateTimeHelper";
import ActionButton from "../../../../Components/Buttons/ActionButton";
import axios from "axios";
import {BOOKINGS_API_URL} from "../../Bookings";
import { isWithinInterval } from "../../../../Helpers/IntervalHelper";
import { useSelector } from "react-redux";

import { validateDay } from "../../../../Common/Availability/AvailabilityValidation";

const AvailabilityCheckWrapper = (props) => {

    const [noNextAvailability, setNoNextAvailability] = useState(false);
    const [daysAheadChecked, setDaysAheadChecked] = useState(30);
    const bookingConfigsLoading = useSelector(state => state.module['BOOKINGS'].bookingForm.bookingConfigsLoading);

    // Reset any potential error state when the user manually chooses another date
    useEffect(() => {
        setNoNextAvailability(false);
    }, [props.bookingDate])

    const handleNextAvailableClick = () => {

        const params = {
            amenityId: props.amenityId,
            unitId: props.unitId,
        };

        axios.get(`${BOOKINGS_API_URL}config/next-available-date`, {params})
            .then(result => {
                if (result.data.nextAvailableDatetime) {
                    props.onBookingDateChange(result.data.nextAvailableDatetime);
                } else {
                    if (result.data.daysAheadChecked) {
                        setDaysAheadChecked(result.data.daysAheadChecked);
                    }
                    setNoNextAvailability(true);
                }
            });
    }


    const shouldShowAvailabilityButton = () => {
        // Do not show Next Available button if we've hit max active bookings,
        // as the user won't be able to book on any day!
        const validationKeys = Object.keys(props.availability.validationMessages);
        if (validationKeys.length
            && validationKeys.includes('max_active_bookings')
        ) {
            return false;
        }

        return props.showNextAvailabilityButton;
    }

    const shouldShowNoAvailabilityMessage = () => {
        if (!props.bookingConfig.is_variable
            && (props.availability.timeslotsRemaining !== null && props.availability.timeslotsRemaining < 1)
        ) {
            return true;
        }

        if (!props.availability.availableIntervals) {
            return false;
        }

        if (!props.availability.availableIntervals.length > 0) {
            return true;
        }

        if (props.bookingConfig.is_variable) {
            return false;
        }

        return !isSameDay(
            dateFromState(props.availability.availableIntervals[0].start),
            dateFromState(props.bookingDate)
        );
    }

    const getAvailabilityMessage = () => {

        if (!props.bookingDate) {
            return '';
        }

        if (!props.bookingConfig.is_variable
            && (props.availability.timeslotsRemaining !== null && props.availability.timeslotsRemaining < 1)
        ) {
            if (props.availability.validationMessages) {
                const messages = Object.values(props.availability.validationMessages);
                return messages[0];
            }
            return "You have exceeded your maximum booking limit.";
        }

        for (let i in props.availability.blockedIntervals) {
            let interval = props.availability.blockedIntervals[i];

            try {
                if (isWithinInterval(props.bookingDate, interval)) {

                    if (!props.bookingConfig) {
                        return 'No Availability';
                    }

                    if (interval.status === 'min-advance') {
                        return `Cannot book less than ${props.bookingConfig.min_advance_period} ${props.bookingConfig.min_advance_units} ahead`
                    }

                    if (interval.status === 'max-advance') {
                        return `Cannot book more than ${props.bookingConfig.max_advance_period} ${props.bookingConfig.max_advance_units} ahead`
                    }
                }
            } catch(error) {
                return 'Date error';
            }
        }
        return 'No Availability';
    }

    if (!shouldShowNoAvailabilityMessage() || bookingConfigsLoading) {
        return props.children;
    }

    if (noNextAvailability) {
        return <p className="no-availability">
            Sorry, there are currently no available dates and times within the next {daysAheadChecked} days. Please try again later.
        </p>
    }


    return <>
        <p className="no-availability padding-bottom-1 availability-validation-error">
            {getAvailabilityMessage()}
        </p>

        {shouldShowAvailabilityButton() && (
            <ActionButton
                title="Next Available"
                className="secondary"
                icon="fa fa-arrow-right"
                onClick={handleNextAvailableClick}
                animateOnClick
            />
        )}
    </>
};

AvailabilityCheckWrapper.propTypes = {
    amenityId: PropTypes.string,
    unitId: PropTypes.string,
    availability: PropTypes.object,
    availableIntervals: PropTypes.array,
    bookingDate: PropTypes.string,
    showNextAvailabilityButton: PropTypes.bool,
    bookingConfig: PropTypes.object,
};

AvailabilityCheckWrapper.defaultProps = {
    showNextAvailabilityButton: true,
};

export default AvailabilityCheckWrapper;
